import { useEffect } from 'react';

interface SEOMetaTagsProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  robots?: string;
  author?: string;
  fbAppId?: string;
}

export default function SEOMetaTags({
  title = "Aram AI - Documenting the AI Solopreneur Journey",
  description = "Follow Aram AI's journey as an AI solopreneur. Learn about AI tools, Armenian AI community, productivity tips, and entrepreneurship insights.",
  keywords = "AI, artificial intelligence, solopreneur, entrepreneurship, Armenian tech, productivity, AI tools, machine learning",
  canonicalUrl,
  ogImage = "https://tezzarida.io/social-preview.png",
  ogType = "website",
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  author = "Aram AI",
  fbAppId = "805249521956122"
}: SEOMetaTagsProps) {
  useEffect(() => {
    // Set document title
    document.title = title;

    // Helper function to set meta tag
    const setMetaTag = (selector: string, attribute: string, content: string) => {
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (attribute === 'property') {
          meta.setAttribute('property', selector.replace('[property="', '').replace('"]', ''));
        } else if (attribute === 'name') {
          meta.setAttribute('name', selector.replace('[name="', '').replace('"]', ''));
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper function to set link tag
    const setLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Basic meta tags
    setMetaTag('[name="description"]', 'name', description);
    setMetaTag('[name="keywords"]', 'name', keywords);
    setMetaTag('[name="robots"]', 'name', robots);
    setMetaTag('[name="author"]', 'name', author);
    setMetaTag('[name="viewport"]', 'name', 'width=device-width, initial-scale=1.0');

    // Open Graph tags
    setMetaTag('[property="og:title"]', 'property', title);
    setMetaTag('[property="og:description"]', 'property', description);
    setMetaTag('[property="og:image"]', 'property', ogImage);
    setMetaTag('[property="og:image:width"]', 'property', '1200');
    setMetaTag('[property="og:image:height"]', 'property', '630');
    setMetaTag('[property="og:image:alt"]', 'property', title);
    setMetaTag('[property="og:type"]', 'property', ogType);
    setMetaTag('[property="og:site_name"]', 'property', 'Aram AI');
    setMetaTag('[property="fb:app_id"]', 'property', fbAppId);
    
    if (canonicalUrl) {
      setMetaTag('[property="og:url"]', 'property', canonicalUrl);
      setLinkTag('canonical', canonicalUrl);
    } else {
      // Set default canonical URL to main domain
      setMetaTag('[property="og:url"]', 'property', 'https://tezzarida.io');
      setLinkTag('canonical', 'https://tezzarida.io');
    }

    // Twitter Card tags
    setMetaTag('[name="twitter:card"]', 'name', 'summary_large_image');
    setMetaTag('[name="twitter:site"]', 'name', '@aram_ai');
    setMetaTag('[name="twitter:creator"]', 'name', '@aram_ai');
    setMetaTag('[name="twitter:title"]', 'name', title);
    setMetaTag('[name="twitter:description"]', 'name', description);
    setMetaTag('[name="twitter:image"]', 'name', ogImage);

    // Additional SEO meta tags
    setMetaTag('[name="theme-color"]', 'name', '#000000');
    setMetaTag('[name="msapplication-TileColor"]', 'name', '#000000');
    setMetaTag('[name="apple-mobile-web-app-capable"]', 'name', 'yes');
    setMetaTag('[name="apple-mobile-web-app-status-bar-style"]', 'name', 'black-translucent');

  }, [title, description, keywords, canonicalUrl, ogImage, ogType, robots, author, fbAppId]);

  return null;
}